import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import MobileMenuButton from '../components/MobileMenuButton';
import PageSkeleton from '../components/PageSkeleton';
import useAnalyticsState from './utils/analyticsStateHooks';
import { normalizeViews, upsertView, removeView, viewId, isValidView, layersForMode } from '../utils/mapViews';
import { useActiveShipments } from '../utils/api-hooks/seaTracking';
import { useActiveFlights, useActiveTrucks } from '../utils/api-hooks/modeTracking';
import { filterModeRows, isSeaRow, rowTransportMode } from '../utils/modeRows';
import { MODE_KEYS, parseModeParam, serializeModeParam, buildWindowSource, countByMode } from '../utils/modeFilter';
import { useWeatherDigestAlerts, useExtremeWeatherAlerts } from '../utils/api-hooks/weatherDigest';
import { useMultiCommodityRegionsData } from '../utils/api-hooks/analytics';
import { convertWeight, mergeVarietyRows } from '../utils/groupByVarietyType';
import { parseMapParams, setStatusParam, LAYER_MODES, STATUS_VALUES } from '../utils/mapUrlParams';
import useCommodityParam from '../hooks/useCommodityParam';
import { hasCommodity } from '../utils/modeRows';
import useMapParams from '../hooks/useMapParams';
import useMapPartyParams from '../hooks/useMapPartyParams';
import useSeasonBundle from '../hooks/useSeasonBundle';
import { canDownloadSeasonPdf } from '../utils/seasonAccessGate';
import { useBiqPageContext } from '../context/BiqPageContext';
import { filterByDays, windowWeight, windowRows } from '../utils/arrivalWindow';
import { arrivalsByDay, padDays } from '../utils/arrivalsByDay';
import { getShipmentCommodityWeight } from '../utils/groupByVarietyType';
import {
  shipmentDestOptionKeys,
  shipmentOriginOptionKeys,
  parseCountryParam,
  serializeCountryParam,
  allCountryParam,
  passesCountryFilter,
} from '../utils/countryFilters';
import {
  SHIPMENT_STATUS_KEYS,
  shipmentStatusKey,
  shipmentImporters,
  shipmentExporters,
  partyOptions,
  toggleSelection,
  passesChipFilters,
} from '../utils/shipmentChipFilters';
import MapSidebar from './components/map/MapSidebar';
import UnifiedMapContainer from './components/map/UnifiedMapContainer';
import GlobalFilterStrip from './components/map/GlobalFilterStrip';
import MapFilterChips from './components/map/MapFilterChips';
import GrowingClusterOverview from './components/map/GrowingClusterOverview';
import { regionMarkerId } from './components/map/weatherConstants';
import AnalyticsChartFullscreenButton from './components/analytics/AnalyticsChartFullscreenButton';
import ContainerSearch from './components/map/ContainerSearch';
import MapArrivalsStrip from './components/map/MapArrivalsStrip';
import MapSeasonSummaryStrip from './components/map/MapSeasonSummaryStrip';
import SeasonSummaryModal from './components/map/SeasonSummaryModal';
import MapAlertsPanel from './components/map/MapAlertsPanel';
import { buildWeatherAlerts, alertStart } from './components/map/alertModel';
import MapAllWidget from './components/map/MapAllWidget';
import ShipmentDetailsPanel from './components/map/ShipmentDetailsPanel';
import { useDismissiblePopover } from './components/home/WeatherMapControls';

// Module-level stable references so memos/effects don't churn on every render
// while userDetails / shipment query data is still loading.
const EMPTY_COMMODITIES = [];
const EMPTY_SHIPMENTS = [];
const EMPTY_OPTIONS = [];
const EMPTY_TOP = [];

const MapPage = () => {
  const { t } = useTranslation();
  const { setPageExtras } = useBiqPageContext() || {};
  const { userDetails, email } = useUser();
  const commodities = userDetails?.preferred_commodities || EMPTY_COMMODITIES;
  // DashboardLayout's mobile drawer toggle, handed down via Outlet context so
  // this page can render its own inline hamburger (mobile only) instead of
  // the standalone header bar — see DashboardLayout's `isMapPage` gate.
  const { onMenuClick, onAskBiq } = useOutletContext() || {};

  // Cross-page commodity (URL → Firestore fallback). Same hook the strip uses so
  // both stay in sync. Other map params (layer, status) live in page state only, never in the URL.
  const [searchParams, setSearchParams] = useMapParams();
  const [selectedCommodity] = useCommodityParam();
  const {
    layer: layerMode,
    status: selectedStatuses,
    region: zoomRegion,
    country: zoomCountry,
    panel: deepLinkPanel,
  } = parseMapParams(searchParams);
  const shipmentsView = layerMode === 'shipments';

  // Sticky party chrome keyed by email so account switches do not flash prior names.
  const [partyChrome, setPartyChrome] = useState({
    email: '',
    ready: false,
    importerOptions: EMPTY_OPTIONS,
    exporterOptions: EMPTY_OPTIONS,
  });
  const partyReady = partyChrome.email === email && partyChrome.ready;
  const importerOptions = partyChrome.email === email ? partyChrome.importerOptions : EMPTY_OPTIONS;
  const exporterOptions = partyChrome.email === email ? partyChrome.exporterOptions : EMPTY_OPTIONS;

  const {
    importer,
    exporter,
    setImporter,
    setExporter,
    clearExporterUrl,
  } = useMapPartyParams({
    importerOptions,
    exporterOptions,
    shipmentsView,
  });

  // Shipment data — party filters re-fetch server-side (render-only).
  const { data, isLoading: shipmentsLoading, isError, refetch } = useActiveShipments({
    importer,
    exporter,
    email,
  });
  // Season Summary has its own request: neither call can block the other.
  const {
    data: seasonBundleData,
    isLoading: seasonBundleLoading,
    isError: seasonBundleError,
  } = useSeasonBundle({ email });
  // Season PDF Download follows map_season_access, not the party data (loading/unknown denies).
  const seasonPdfAllowed = canDownloadSeasonPdf(seasonBundleData?.season_access);
  // Stable fallback: inline `|| []` allocates a new array every render while
  // data is undefined, which churns arriving* memos and re-fires setPageExtras
  // (Maximum update depth — loud under local StrictMode / cold load).
  const shipments = data?.data?.shipments ?? EMPTY_SHIPMENTS;
  const topImporters = partyReady ? (data?.data?.top_importers ?? EMPTY_TOP) : null;

  useEffect(() => {
    const payload = data?.data;
    if (!payload) return;
    const ready = Array.isArray(payload.importer_options);
    setPartyChrome({
      email,
      ready,
      importerOptions: ready
        ? payload.importer_options
        : EMPTY_OPTIONS,
      exporterOptions: Array.isArray(payload.exporter_options)
        ? payload.exporter_options
        : EMPTY_OPTIONS,
    });
  }, [data, email]);
  // Exporter is Shipments-only — drop it from the URL when leaving that layer.
  useEffect(() => {
    if (!shipmentsView) clearExporterUrl();
  }, [shipmentsView, clearExporterUrl]);

  const partyFilterProps = {
    partyReady,
    importer,
    importerOptions,
    onImporterChange: setImporter,
    showExporter: shipmentsView,
    exporter,
    exporterOptions,
    onExporterChange: setExporter,
  };
  // Air + truck are map-only layers: they never feed the arrival windows,
  // scorecards or sidebar. A failed/404 endpoint is a quiet empty list.
  const { data: flightsData, isError: flightsError } = useActiveFlights();
  const { data: trucksData, isError: trucksError } = useActiveTrucks();
  // On a failed refetch react-query keeps the old data: show nothing instead.
  const flightsLive = (!flightsError && flightsData?.data?.flights) || EMPTY_SHIPMENTS;
  // DEV-only preview (see src/dev); the guarded import is dropped from builds.
  const [devPreview, setDevPreview] = useState(null);
  useEffect(() => {
    if (import.meta.env.DEV) {
      import('../dev/airPreview').then((m) => setDevPreview(() => m.applyAirPreview));
    }
  }, []);
  const flights = useMemo(() => (devPreview ? devPreview(flightsLive) : flightsLive), [devPreview, flightsLive]);
  const trucks = (!trucksError && trucksData?.data?.trucks) || EMPTY_SHIPMENTS;

  // Fullscreen toggle (mirrors Analytics' ?fullscreen=1). DashboardLayout
  // hides the app sidebar and TopBar when this is set; we hide the
  // ScorecardsBar and MapSidebar here so the map fills the viewport.
  const isFullscreen = searchParams.get('fullscreen') === '1';
  const setFullscreen = useCallback(
    (next) => {
      const params = new URLSearchParams(searchParams);
      if (next) params.set('fullscreen', '1');
      else params.delete('fullscreen');
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Weather alerts — driven by the commodity selector. When a commodity is picked,
  // fetch only that one's alerts; otherwise fall back to all preferred.
  const alertCommodities = useMemo(
    () => (selectedCommodity ? [selectedCommodity] : commodities),
    [selectedCommodity, commodities],
  );
  const { alerts: weatherAlerts } = useWeatherDigestAlerts(alertCommodities);
  const { alerts: extremeAlerts } = useExtremeWeatherAlerts(alertCommodities);

  // Harvest (season-shift) alerts — reuse the same cached producing-regions query the
  // map uses; the backend already gates alert.active to in-season/<=8-weeks-before-start.
  // Use the SAME `email` accessor as UnifiedMapContainer so the react-query cache key
  // matches (dedupes the fetch, and keeps hover/click ids in sync with rendered markers).
  const { regions: alertRegions } = useMultiCommodityRegionsData(
    email,
    alertCommodities,
    null,
  );
  const harvestAlerts = useMemo(
    () => (alertRegions || []).filter(
      (r) => r.alert?.active && r.latitude != null && r.longitude != null,
    ),
    [alertRegions],
  );

  const setLayerMode = useCallback(
    (next) => {
      const params = new URLSearchParams(searchParams);
      if (next && next !== LAYER_MODES.ALL) params.set('layer', next);
      else params.delete('layer');
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Status filter setter — same URL contract as GlobalFilterStrip's internal
  // handleStatusChange (all-selected or empty both clear the param, since
  // "no status param" already means "show all"). Mobile-only consumer: the
  // "Map controls" panel inside UnifiedMapContainer reuses this so the status
  // filter stays wired to the same URL state whether it's toggled from the
  // top strip (desktop) or from next to the map controls (mobile).
  const setSelectedStatuses = useCallback(
    (next) => setStatusParam(searchParams, setSearchParams, next),
    [searchParams, setSearchParams],
  );

  // Selected growing cluster. Owned here, not in the map, so its overview can
  // sit in the overlay column below the filters like the shipment panel does.
  // Selecting another cluster replaces this one, so closing and re-selecting
  // are the same operation and only one can ever be open.
  const [selectedCluster, setSelectedCluster] = useState(null);
  // Bumped to ask the map to fit the cluster; a fresh object per press so
  // pressing "Zoom to regions" twice still re-fits (same trick focusedRegion
  // uses for re-clicking one Harvest Alerts card).
  const [zoomToCluster, setZoomToCluster] = useState(null);
  const zoomSeqRef = useRef(0);

  // Groups the user chose to open in place, held as region ids rather than
  // group keys — a key encodes membership, so it changes the moment the data
  // regroups. See UnifiedMapContainer.
  const [expandedRegionIds, setExpandedRegionIds] = useState(() => new Set());
  // "Reset" is an event, not a state, so it travels as a counter: resetting
  // twice in a row has to work.
  const [resetViewSeq, setResetViewSeq] = useState(0);

  /**
   * Marks a group's locations as drawn individually.
   *
   * Shared by "Zoom to regions" and the per-row magnifier — both reveal a
   * group's pins as part of what they do, since grouping comes from the data
   * and zooming alone no longer separates anything.
   *
   * Declared above its callers on purpose: a `useCallback` dependency array is
   * evaluated during render, so a handler listing this below its own
   * definition would throw before the page painted.
   */
  const expandCluster = useCallback((cluster) => {
    if (!cluster?.regions?.length) return;
    setExpandedRegionIds((prev) => {
      const next = new Set(prev);
      for (const r of cluster.regions) next.add(regionMarkerId(r));
      return next;
    });
  }, []);

  /** Puts a group's locations back behind its single pin. */
  const collapseCluster = useCallback((cluster) => {
    if (!cluster?.regions?.length) return;
    setExpandedRegionIds((prev) => {
      if (!prev.size) return prev;
      const next = new Set(prev);
      for (const r of cluster.regions) next.delete(regionMarkerId(r));
      return next.size === prev.size ? prev : next;
    });
  }, []);

  /**
   * Whether the open panel's own group is drawn expanded — which is what
   * swaps its header from offering Expand to offering Reset.
   */
  const selectedClusterExpanded = useMemo(
    () => !!selectedCluster?.regions?.length
      && selectedCluster.regions.every((r) => expandedRegionIds.has(regionMarkerId(r))),
    [selectedCluster, expandedRegionIds],
  );

  const handleZoomToCluster = useCallback((cluster) => {
    if (!cluster) return;
    zoomSeqRef.current += 1;
    setZoomToCluster({ ...cluster, _seq: zoomSeqRef.current });
    // Expand as well as fly. Grouping comes from the data now, so zoom alone
    // no longer separates the pins — that used to arrive for free when the
    // grouping was recomputed in screen space at every zoom.
    expandCluster(cluster);
    setSelectedCluster(null);
  }, [expandCluster]);

  const handleResetView = useCallback(() => {
    setExpandedRegionIds((prev) => (prev.size ? new Set() : prev));
    setSelectedCluster(null);
    setResetViewSeq((n) => n + 1);
  }, []);

  // A selection must not outlive the grouping it describes: leaving the layers
  // that draw regions, or changing the commodity or status filters, all
  // rebuild the groups and strand it.
  //
  // Keyed on primitives — never on the derived region arrays. Those get a new
  // identity on every render, so an effect watching them runs on every render
  // and would clear the selection on the same render the click set it.
  const clusterFilterKey = `${layerMode}|${selectedCommodity}|${selectedStatuses.join(',')}`;
  useEffect(() => {
    setSelectedCluster(null);
    // Expansions describe the same grouping and go stale with it. Left behind,
    // the Reset pill would sit there offering to undo nothing.
    setExpandedRegionIds((prev) => (prev.size ? new Set() : prev));
  }, [clusterFilterKey]);

  // Season summary. Deliberately ungated: the modal handles permissions
  // itself, showing an Elite upsell and blurred placeholders to users without
  // access. Gating the button here hid that upsell from the very people it is
  // meant for.
  const [seasonSummaryOpen, setSeasonSummaryOpen] = useState(false);

  // Saved views ("Combine layers"). Stored in the same per-user Firestore
  // document the commodity selection uses, so they survive a reload and
  // follow the user between devices — the spec asks for both.
  const { state: analyticsState, updateState: updateAnalyticsState } = useAnalyticsState();
  const savedViews = useMemo(
    () => normalizeViews(analyticsState?.mapViews),
    [analyticsState?.mapViews],
  );

  // Which view is showing, if any. Deliberately NOT persisted: reopening the
  // map on a saved combination the user has since forgotten about is more
  // surprising than landing on the default layer.
  const [activeViewId, setActiveViewId] = useState(null);

  const activeViewIdRef = useRef(null);
  const activeViewLayersRef = useRef(null);

  const activeView = useMemo(
    () => savedViews.find((v) => v.id === activeViewId) || null,
    [savedViews, activeViewId],
  );

  useEffect(() => {
    activeViewIdRef.current = activeViewId;
    activeViewLayersRef.current = activeView?.layers || null;
  }, [activeViewId, activeView]);

  // The layers actually drawn: a live view's set, otherwise whatever the
  // selected layer mode implies. One value for the map to read, so the two
  // selection styles cannot disagree about what is on screen.
  const activeLayers = useMemo(
    () => (activeView ? activeView.layers : layersForMode(layerMode)),
    [activeView, layerMode],
  );

  // Filter chrome follows what is drawn, not the layer tab that happens to be
  // selected underneath. Without this a view like "Growing + Shipments" kept
  // whatever the previous single layer implied — Weather's search hidden, the
  // Status dropdown in the wrong place, no Arrivals — because every gate below
  // was written against `layerMode` before views existed.
  //
  // `viewHas` answers from the live view when there is one and returns null
  // otherwise, so each call site keeps its original built-in rule verbatim
  // rather than being re-derived and quietly changed.
  const viewHas = useCallback(
    (layer) => (activeView ? activeView.layers.includes(layer) : null),
    [activeView],
  );

  // Arrivals is a shipment figure, so it shows only on the layers that draw
  // shipments — the same split UnifiedMapContainer uses for `showShipments`.
  const showArrivals = viewHas(LAYER_MODES.SHIPMENTS)
    ?? (layerMode === LAYER_MODES.ALL || layerMode === LAYER_MODES.SHIPMENTS);

  // Weather only: the Growing design has no alerts widget, and All does not
  // show one either.
  const showAlertsPanel = viewHas(LAYER_MODES.WEATHER)
    ?? (layerMode === LAYER_MODES.WEATHER);

  // Whether a forecast timeline is docked along the map's bottom edge, which
  // the Arrivals panel has to stop short of. Reads `activeLayers` — what is
  // actually drawn — which is the same value the map itself gates the
  // timeline on. Deliberately NOT the alerts test above: All draws weather
  // too, so it gets a timeline while getting no alerts widget, and keying
  // this off the alerts condition left All — the one mode the clearance was
  // asked for — with no clearance at all.
  const hasWeatherTimeline = activeLayers.includes(LAYER_MODES.WEATHER);

  // Save closes the popover (the rail does that), stores the view and makes
  // it current, so the map changes as soon as it appears in the rail.
  const handleCombineSave = useCallback((layers) => {
    const next = upsertView(savedViews, layers);
    if (next === savedViews) return;            // nothing valid was ticked
    updateAnalyticsState?.({ mapViews: next });
    setActiveViewId(viewId(layers));
  }, [savedViews, updateAnalyticsState]);

  // Editing changes the layer set, and with it the id and the generated name.
  // The old entry is removed and the new one saved, so a view edited into a
  // combination that already exists merges with it rather than leaving two
  // tiles that draw the same map.
  const handleUpdateView = useCallback((id, layers) => {
    if (!isValidView(layers)) return;
    const next = upsertView(removeView(savedViews, id), layers);
    updateAnalyticsState?.({ mapViews: next });
    // If the edited view was the one showing, stay on it under its new id so
    // the map updates immediately rather than falling back to a layer tab.
    setActiveViewId((cur) => (cur === id ? viewId(layers) : cur));
  }, [savedViews, updateAnalyticsState]);

  const handleDeleteView = useCallback((id) => {
    updateAnalyticsState?.({ mapViews: removeView(savedViews, id) });
    // Deleting the live view leaves nothing drawing the map, so fall back to
    // the layer mode the rail still has selected underneath.
    setActiveViewId((cur) => (cur === id ? null : cur));
  }, [savedViews, updateAnalyticsState]);

  // Picking a layer tab leaves whatever view was showing — the rail cannot
  // present both as current, and the tab is the more explicit action.
  const handleLayerModeSelect = useCallback((next) => {
    setActiveViewId(null);
    setLayerMode(next);
  }, [setLayerMode]);

  // Weather's tile layer and hazard types. Held here, not in the map, because
  // their chips render in the overlay column below the filter strip.
  const [activeLayer, setActiveLayer] = useState('temp');
  const [activeEventTypes, setActiveEventTypes] = useState([]);
  const handleEventTypeToggle = useCallback((eventType) => {
    setActiveEventTypes((prev) => (
      prev.includes(eventType) ? prev.filter((et) => et !== eventType) : [...prev, eventType]
    ));
  }, []);

  // Growing's Production status chip. It is the same `status` URL param the
  // strip's Status dropdown writes — one filter, surfaced in the place that
  // suits the layer — so the chip and the dropdown can never drift apart.
  // `selected: null` means unrestricted, matching the chip row's contract.
  const productionStatusFilter = useMemo(
    () => ({
      keys: STATUS_VALUES,
      selected: selectedStatuses.length > 0 && selectedStatuses.length < STATUS_VALUES.length
        ? selectedStatuses
        : null,
      onToggle: (id) => {
        const current = selectedStatuses.length > 0 ? selectedStatuses : STATUS_VALUES;
        setSelectedStatuses(
          current.includes(id) ? current.filter((s) => s !== id) : [...current, id],
        );
      },
      onSelectAll: () => setSelectedStatuses(STATUS_VALUES),
      onSelectNone: () => setSelectedStatuses([]),
    }),
    [selectedStatuses, setSelectedStatuses],
  );

  // UI state (page-local; not part of cross-page URL contract yet)
  const [activePanel, setActivePanel] = useState(null);
  const [weightUnit, setWeightUnit] = useState('boxes');
  const [allPanelOpen, setAllPanelOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const toggleExpandedSection = useCallback(
    (id) => setExpandedSection((prev) => (prev === id ? null : id)),
    [],
  );
  const [selectedShipment, setSelectedShipment] = useState(null);
  // The details popup takes the Arrivals widget's slot in the overlay column,
  // so the widget drops back to its pill while a shipment is selected. The
  // user's own open/closed choice is kept in `allPanelOpen` rather than being
  // reset, so closing the popup restores whatever they had open.
  const arrivalsOpen = allPanelOpen && !selectedShipment;
  // Wraps the pill AND the panel, so toggling via the pill counts as inside
  // and does not close-then-reopen on the same press.
  const arrivalsWrapRef = useRef(null);
  useDismissiblePopover(arrivalsWrapRef, arrivalsOpen, setAllPanelOpen);
  // Owned here because the container search moved into the persistent filter
  // strip, while the shipment card it affects still renders inside the map.
  const [containerDetailOpen, setContainerDetailOpen] = useState(false);
  // Region hovered in the Harvest Alerts panel → its map pin enlarges.
  const [hoveredRegionId, setHoveredRegionId] = useState(null);
  // Region whose Harvest Alerts card was clicked → map flies to it + opens its tooltip.
  // A per-click seq makes every click a fresh object reference, so re-clicking the SAME
  // card (after manually closing the popup) still re-triggers the fly-to + open.
  const [focusedRegion, setFocusedRegionState] = useState(null);
  const focusSeqRef = useRef(0);
  const setFocusedRegion = useCallback((r) => {
    focusSeqRef.current += 1;
    setFocusedRegionState(r ? { ...r, _seq: focusSeqRef.current } : null);
    // Region pins only render where Growing is drawn. Without this, clicking a
    // Harvest Alerts card on a layer without them flew the map to a spot with
    // no pin and no popup to open.
    //
    // A live view decides its own layers, so switching the mode underneath it
    // would change nothing on screen — the view has to be left first, or the
    // card still lands on an empty map.
    if (!r) return;
    if (activeViewIdRef.current) {
      if (!activeViewLayersRef.current?.includes(LAYER_MODES.GROWING)) {
        setActiveViewId(null);
        setLayerMode(LAYER_MODES.GROWING);
      }
      return;
    }
    if (layerMode !== LAYER_MODES.ALL && layerMode !== LAYER_MODES.GROWING) {
      setLayerMode(LAYER_MODES.GROWING);
    }
  }, [layerMode, setLayerMode]);


  // Hydrate activePanel from `?panel` so deep-links (e.g. clicking the Weather
  // Alerts scorecard on Analytics) actually open the matching sidebar on /map.
  // Re-runs on URL change so back/forward + new deep-links also apply.
  useEffect(() => {
    if (deepLinkPanel) setActivePanel(deepLinkPanel);
  }, [deepLinkPanel]);

  // Origin / Destination filters (`origin`, `dest`); memoised on the raw strings.
  const originRaw = searchParams.get('origin');
  const destRaw = searchParams.get('dest');
  const originSel = useMemo(() => parseCountryParam(originRaw), [originRaw]);
  const destSel = useMemo(() => parseCountryParam(destRaw), [destRaw]);

  // Transport modes (`mode`): absent = all three (Sea + Air + Truck).
  const modeRaw = searchParams.get('mode');
  const modeSel = useMemo(() => parseModeParam(modeRaw), [modeRaw]);
  const seaOn = modeSel.keys.has('sea');
  const airOn = modeSel.keys.has('air');
  const truckOn = modeSel.keys.has('truck');
  const selectedModes = useMemo(() => MODE_KEYS.filter((k) => modeSel.keys.has(k)), [modeSel]);

  // Options from unfiltered rows of the selected modes + defaults + URL keys, so a selection never vanishes.
  // Option keys come only from the voyage stops (heads) carrying the selected commodity.
  const optionRows = useMemo(
    () => [
      ...(seaOn ? shipments : EMPTY_SHIPMENTS),
      ...(airOn ? flights : EMPTY_SHIPMENTS),
      ...(truckOn ? trucks : EMPTY_SHIPMENTS),
    ],
    [shipments, flights, trucks, seaOn, airOn, truckOn],
  );
  const originKeys = useMemo(() => {
    const keys = new Set([
      ...(seaOn ? shipments : EMPTY_SHIPMENTS)
        .flatMap((r) => shipmentOriginOptionKeys(r, selectedCommodity)),
      ...[...(airOn ? flights : EMPTY_SHIPMENTS), ...(truckOn ? trucks : EMPTY_SHIPMENTS)]
        .filter((r) => !selectedCommodity || hasCommodity(r, selectedCommodity))
        .map(() => 'PERU'),
    ]);
    originSel.keys.forEach((k) => keys.add(k));
    keys.delete('');
    return [...keys].sort();
  }, [shipments, flights, trucks, seaOn, airOn, truckOn, originSel, selectedCommodity]);
  const destKeys = useMemo(() => {
    const keys = new Set(optionRows.flatMap((r) => shipmentDestOptionKeys(r, selectedCommodity)));
    destSel.keys.forEach((k) => keys.add(k));
    keys.delete('');
    return [...keys].sort();
  }, [optionRows, destSel, selectedCommodity]);

  const setModeKeys = useCallback(
    (next) => {
      const params = new URLSearchParams(searchParams);
      const value = serializeModeParam(next);
      if (value == null) params.delete('mode');
      else params.set('mode', value);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  const modeFilter = useMemo(
    () => ({
      keys: MODE_KEYS,
      selectedKeys: selectedModes,
      mode: modeSel.mode,
      onChange: setModeKeys,
      onSelectAll: () => setModeKeys(MODE_KEYS),
    }),
    [selectedModes, modeSel, setModeKeys],
  );
  const setCountryParam = useCallback(
    (name, value) => {
      const params = new URLSearchParams(searchParams);
      if (value == null) params.delete(name);
      else params.set(name, value);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  const setOriginKeys = useCallback(
    (next) => setCountryParam('origin', serializeCountryParam(next, originKeys)),
    [setCountryParam, originKeys],
  );
  const setDestKeys = useCallback(
    (next) => setCountryParam('dest', serializeCountryParam(next, destKeys)),
    [setCountryParam, destKeys],
  );
  // Explicit "All" writes the unrestricted state even with no option list loaded.
  const selectAllOrigin = useCallback(() => setCountryParam('origin', allCountryParam()), [setCountryParam]);
  const selectAllDest = useCallback(() => setCountryParam('dest', allCountryParam()), [setCountryParam]);
  const originFilter = useMemo(
    () => ({
      keys: originKeys,
      selectedKeys: originSel.mode === 'all' ? originKeys : [...originSel.keys],
      mode: originSel.mode,
      onChange: setOriginKeys,
      onSelectAll: selectAllOrigin,
    }),
    [originKeys, originSel, setOriginKeys, selectAllOrigin],
  );
  const destFilter = useMemo(
    () => ({
      keys: destKeys,
      selectedKeys: destSel.mode === 'all' ? destKeys : [...destSel.keys],
      mode: destSel.mode,
      onChange: setDestKeys,
      onSelectAll: selectAllDest,
    }),
    [destKeys, destSel, setDestKeys, selectAllDest],
  );
  const countryPredicate = useCallback(
    (s) => passesCountryFilter(s, originSel, destSel),
    [originSel, destSel],
  );

  // Chip row selections. Unlike origin/dest these stay in component state
  // rather than the URL — they are a browsing aid, not a shareable view, and
  // the importer/exporter lists are too long to serialize sensibly.
  // `null` means unrestricted; see toggleSelection in shipmentChipFilters.
  const [statusSel, setStatusSel] = useState(null);
  const [importerSel, setImporterSel] = useState(null);
  const [exporterSel, setExporterSel] = useState(null);

  // Named `chip*` to stand apart from the party-chrome options above, which
  // feed GlobalFilterStrip's PartySelects. These are derived from the loaded
  // rows and feed the chip row; the two lists are not interchangeable.
  const chipImporterOptions = useMemo(() => partyOptions(optionRows, shipmentImporters), [optionRows]);
  const chipExporterOptions = useMemo(() => partyOptions(optionRows, shipmentExporters), [optionRows]);

  const chipPredicate = useCallback(
    (s) => passesChipFilters(s, {
      statuses: statusSel,
      importers: importerSel,
      exporters: exporterSel,
    }),
    [statusSel, importerSel, exporterSel],
  );

  // One prop for the whole chip row. Option lists come from the loaded rows;
  // the status labels are applied in UnifiedMapContainer, which owns the chips.
  const chipFilters = useMemo(
    () => ({
      status: {
        keys: SHIPMENT_STATUS_KEYS,
        selected: statusSel,
        onToggle: (id) => setStatusSel((prev) => toggleSelection(prev, id)),
        onSelectAll: () => setStatusSel(null),
        onSelectNone: () => setStatusSel([]),
      },
      importer: {
        options: chipImporterOptions,
        selected: importerSel,
        onToggle: (id) => setImporterSel((prev) => toggleSelection(prev, id)),
        onSelectAll: () => setImporterSel(null),
        onSelectNone: () => setImporterSel([]),
      },
      exporter: {
        options: chipExporterOptions,
        selected: exporterSel,
        onToggle: (id) => setExporterSel((prev) => toggleSelection(prev, id)),
        onSelectAll: () => setExporterSel(null),
        onSelectNone: () => setExporterSel([]),
      },
    }),
    [statusSel, importerSel, exporterSel, chipImporterOptions, chipExporterOptions],
  );

  // Commodity, then country, then the chip row: single point feeding scorecards and map markers.
  const filteredShipments = useMemo(() => {
    const byCommodity = !selectedCommodity
      ? shipments
      : shipments.filter((s) => {
          if (!s.commodities) return false;
          return s.commodities
            .split(',')
            .some((c) => c.trim().toLowerCase() === selectedCommodity.toLowerCase());
        });
    return byCommodity.filter(countryPredicate).filter(chipPredicate);
  }, [shipments, selectedCommodity, countryPredicate, chipPredicate]);

  const filteredFlights = useMemo(
    () => filterModeRows(flights, 'air', selectedCommodity, originSel, destSel).filter(chipPredicate),
    [flights, selectedCommodity, originSel, destSel, chipPredicate],
  );
  const filteredTrucks = useMemo(
    () => filterModeRows(trucks, 'truck', selectedCommodity, originSel, destSel).filter(chipPredicate),
    [trucks, selectedCommodity, originSel, destSel, chipPredicate],
  );

  // ONE source for the scorecards and sidebar: the selected modes' rows (each
  // already commodity/country filtered). Sea only is the sea array itself.
  const windowSource = useMemo(
    () => buildWindowSource(selectedModes, filteredShipments, filteredFlights, filteredTrucks),
    [selectedModes, filteredShipments, filteredFlights, filteredTrucks],
  );

  // Compute arrivals for 3, 7, and 10 day windows
  // One shared `now` so the three windows are consistent and cumulative.
  const arrivalWindows = useMemo(() => {
    const now = new Date();
    const at = (days) => filterByDays(windowSource, days, now, selectedCommodity);
    return { arriving3: at(3), arriving7: at(7), arriving10: at(10) };
  }, [windowSource, selectedCommodity]);
  const { arriving3, arriving7, arriving10 } = arrivalWindows;

  // Scorecard weights follow the commodity selector, not just the shipment list.
  const weightOf = useCallback(
    (entries) => windowWeight(entries, selectedCommodity),
    [selectedCommodity],
  );

  // Active arrivals panel tracks which time window (3, 7, or 10)
  const arrivingShipments = windowRows(
    activePanel === 'arrivals3' ? arriving3
      : activePanel === 'arrivals10' ? arriving10
      : activePanel === 'arrivals' ? arriving7
      : [],
  );

  // Alerts worth surfacing: active, and high market impact. Kept as the list
  // rather than just its length, because the weather strip shows the count and
  // opens the rows behind it — deriving each separately is how the two end up
  // disagreeing. Same filter as before; only the shape changed.
  const activeWeatherAlerts = useMemo(() => {
    const isHighImpact = (a) => ['high', 'alto'].includes((a.market_impact || '').toLowerCase());
    const extreme = (extremeAlerts || []).filter(
      (a) => String(a.status).toLowerCase() === 'active' && isHighImpact(a),
    );
    const digest = (weatherAlerts || []).filter((a) => {
      const active = a.is_active;
      if (active === false || active === 'false' || active === 'False' || active === 0) return false;
      return isHighImpact(a);
    });
    return [...extreme, ...digest];
  }, [extremeAlerts, weatherAlerts]);

  // High-impact only. The BiQ payload and the All widget's "· N high" both
  // mean that specifically, so this keeps its original definition.
  const alertsCount = activeWeatherAlerts.length;

  // The Alerts panel lists every ACTIVE alert, not only high-impact ones —
  // severity is a field on the card, so filtering to one level would leave the
  // Medium and Low rows with nowhere to appear.
  const panelWeatherAlerts = useMemo(
    () => buildWeatherAlerts(extremeAlerts, weatherAlerts),
    [extremeAlerts, weatherAlerts],
  );

  // Region alerts: the same rows the map pins and the sidebar list use, newest
  // first per the design. `harvestAlerts` is already filtered to active ones.
  const panelRegionAlerts = useMemo(
    () => [...harvestAlerts].sort((a, b) => {
      const ta = alertStart(a.alert) ?? alertStart(a);
      const tb = alertStart(b.alert) ?? alertStart(b);
      if (ta == null) return 1;
      if (tb == null) return -1;
      return tb - ta;
    }),
    [harvestAlerts],
  );

  // "AHEAD OF SCHEDULE" / "BEHIND SCHEDULE" / "ON SCHEDULE", from the signed
  // week delta the harvest feed carries (negative = earlier than last season).
  const regionStatusLabel = useCallback((r) => {
    const n = Number(r?.alert?.weeks_delta);
    if (!Number.isFinite(n) || n === 0) return t('map.alerts.onSchedule', 'ON SCHEDULE');
    return n < 0
      ? t('map.alerts.aheadOfSchedule', 'AHEAD OF SCHEDULE')
      : t('map.alerts.behindSchedule', 'BEHIND SCHEDULE');
  }, [t]);

  // Publish map scorecard extras for BiQ. Depend on primitive counts/weights
  // (not arriving* array identities) so a context re-render cannot re-fire this.
  const arriving3Count = arriving3.length;
  const arriving7Count = arriving7.length;
  const arriving10Count = arriving10.length;
  const arriving3Boxes = convertWeight(weightOf(arriving3), 'boxes');
  const arriving7Boxes = convertWeight(weightOf(arriving7), 'boxes');
  const arriving10Boxes = convertWeight(weightOf(arriving10), 'boxes');
  const harvestAlertsCount = harvestAlerts.length;
  const modesKey = selectedModes.join(',');
  const by3 = countByMode(arriving3);
  const by7 = countByMode(arriving7);
  const by10 = countByMode(arriving10);
  const byModeKey = [by3, by7, by10].map((b) => `${b.sea}/${b.air}/${b.truck}`).join('|');

  // Sea off: drop any selected vessel so no sea card or route lingers. Air and
  // truck rows now use the same panel, so only a sea selection is cleared here —
  // unchecking Sea used to close an open flight's details too.
  useEffect(() => {
    if (!seaOn) setSelectedShipment((s) => (isSeaRow(s) ? null : s));
  }, [seaOn]);

  // Likewise for the mode checkboxes: an unchecked mode closes its own panel.
  useEffect(() => {
    setSelectedShipment((s) => {
      const m = rowTransportMode(s);
      return m !== 'sea' && !modeSel.keys.has(m) ? null : s;
    });
  }, [modeSel]);

  useEffect(() => {
    if (!setPageExtras) return undefined;
    setPageExtras({
      commodity: selectedCommodity || '',
      layer: layerMode,
      panel: activePanel || '',
      modes: modesKey ? modesKey.split(',') : [],
      arriving: {
        d3: { shipments: arriving3Count, boxes: arriving3Boxes, by_mode: by3 },
        d7: { shipments: arriving7Count, boxes: arriving7Boxes, by_mode: by7 },
        d10: { shipments: arriving10Count, boxes: arriving10Boxes, by_mode: by10 },
      },
      weather_alerts_active: alertsCount,
      harvest_alerts: harvestAlertsCount,
      // What the details popup's "Ask BiQ" button relies on: the shipment is
      // already in BiQ's page context by the time the popup opens, so the
      // button only has to open the chat. Keyed on the container number so
      // this effect re-fires on selection rather than on every row identity.
      selected_shipment: selectedShipment
        ? {
          container: selectedShipment.container_number || '',
          commodities: selectedShipment.commodities || '',
          origin: selectedShipment.origin_country || selectedShipment.origin || '',
          destination: selectedShipment.arrival_port || '',
          vessel: selectedShipment.current_vessel || selectedShipment.vessel || '',
          status: shipmentStatusKey(selectedShipment),
          containers: selectedShipment.total_containers ?? null,
          delay_days: selectedShipment.delay_days ?? null,
        }
        : null,
    });
  }, [
    setPageExtras,
    selectedCommodity,
    layerMode,
    activePanel,
    modesKey,
    selectedShipment,
    byModeKey,
    arriving3Count,
    arriving7Count,
    arriving10Count,
    arriving3Boxes,
    arriving7Boxes,
    arriving10Boxes,
    alertsCount,
    harvestAlertsCount,
  ]);

  // Clear extras only on unmount — not on every deps change (that paired with
  // setPageExtras above and amplified update storms under StrictMode).
  useEffect(() => {
    if (!setPageExtras) return undefined;
    return () => setPageExtras(null);
  }, [setPageExtras]);

  // Expanded "All" panel rows. Everything here reuses the same filtered data the
  // map and strip already render, so the panel can never disagree with them.
  // `filterByDays` with a very wide horizon is how a raw row list becomes the
  // { card, units } entries that `weightOf` understands.
  // The Arrivals pop-up's own window. Not persisted and not in the URL: it is
  // a way of reading the panel, not a filter on the map.
  const [arrivalDays, setArrivalDays] = useState(7);

  const arrivalsByWindow = useMemo(
    () => ({ 3: arriving3, 7: arriving7, 10: arriving10 }),
    [arriving3, arriving7, arriving10],
  );

  /** Tab figures. Every window carries its own, so the three can be compared. */
  const arrivalWindowStats = useMemo(
    () => [3, 7, 10].map((days) => ({
      days,
      total: convertWeight(weightOf(arrivalsByWindow[days]), weightUnit),
      count: arrivalsByWindow[days].length,
    })),
    [arrivalsByWindow, weightUnit, weightOf],
  );

  /**
   * The selected window, grouped into the days it lands on.
   *
   * `padDays` fills the days nothing arrives on: the list reads as a calendar,
   * and a silent gap invites the question of whether that day is empty or
   * missing.
   */
  const arrivalDayGroups = useMemo(() => {
    const rowWeight = (row) => convertWeight(
      getShipmentCommodityWeight(row, selectedCommodity) ?? 0,
      weightUnit,
    );
    const rows = windowRows(arrivalsByWindow[arrivalDays]).map((row) => ({
      ...row,
      value: rowWeight(row),
    }));
    // A day lists what is arriving, not which hulls carry it: the same
    // variety on three vessels is one line of interest, where three vessel
    // names are three lines the reader has to add up. `mergeVarietyRows` is
    // the function the Arrivals sidebar already groups by, so the panel and
    // the sidebar cannot disagree about what counts as one variety. The
    // shipments behind each line are kept as `sources`, which is where the
    // design's "(2)" comes from.
    const byVariety = (dayRows) => mergeVarietyRows(
      dayRows.map((r) => ({ variety: r.varieties || '—', weight_kg: r.value, __row: r })),
    ).map((v, i) => ({
      key: `${v.variety}|${i}`,
      label: v.variety,
      count: v.sources.length,
      value: v.weight_kg,
    }));

    return padDays(arrivalsByDay(rows, (r) => r.value), arrivalDays)
      .map((d) => ({ ...d, rows: byVariety(d.rows) }));
  }, [arrivalsByWindow, arrivalDays, selectedCommodity, weightUnit]);

  const arrivalTotal = useMemo(
    () => convertWeight(weightOf(arrivalsByWindow[arrivalDays]), weightUnit),
    [arrivalsByWindow, arrivalDays, weightUnit, weightOf],
  );

  if (shipmentsLoading) {
    return <PageSkeleton shape="map" />;
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-500 mb-3">{t('seaTracking.error', 'Error loading data')}</p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-bloom-green text-white rounded-lg text-sm hover:bg-[#12b152]"
          >
            {t('common.update', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  // Desktop (lg+): the map is the page. No padding, border or radius — it runs
  // edge to edge in the content area beside the app sidebar, and every control
  // floats on top of it as an absolutely positioned overlay, inset 24px per the
  // design. Mobile (<lg) keeps the stacked, scrolling layout.

  const makeContainerSearch = (heightClass) => (
    <ContainerSearch
      variant="strip"
      heightClass={heightClass}
      activeShipments={filteredShipments}
      onResultSelect={(result) => {
        if (!result) return;
        const fromActive = filteredShipments.find((s) =>
          s?.container_number && result.container_number
          && String(s.container_number).toUpperCase() === String(result.container_number).toUpperCase()
        );
        setSelectedShipment(fromActive ? { ...result, ...fromActive } : result);
      }}
      onDetailPanelChange={setContainerDetailOpen}
    />
  );
  // Container search finds a shipment, so it belongs to the layers that draw
  // them. Weather has no container on screen for it to select.
  const searchable = viewHas(LAYER_MODES.SHIPMENTS) ?? (layerMode !== LAYER_MODES.WEATHER);
  const containerSearch = searchable ? makeContainerSearch('h-[40px]') : null;
  // Where the chip row lives. Fullscreen only: the design's rule there is one
  // line of filters with the pop-up pills beneath.
  //
  // Weather used to hoist too, to fill the slot its hidden container search
  // leaves empty. That stopped working once All and Weather gained the
  // Production status chip: the strip's slot took it first and the weather
  // switch wrapped underneath, off the commodity selector's left edge. The
  // switch now keeps its own full line in the chip row (see MapFilterChips),
  // which is what the design asks for and what the slot could not give it.
  const hoistChips = isFullscreen;
  const mobileContainerSearch = searchable ? makeContainerSearch('h-[44px]') : null;

  // Built once and placed in one of two slots depending on fullscreen — see
  // the two mount points below.
  // Reset view. Hangs below the strip's fullscreen toggle, right-aligned, as
  // the design stacks them. Positioned absolutely rather than placed in the
  // overlay column: there it mounted ABOVE the cluster panel, so the first
  // Expand click pushed that panel down and read as the popup jumping.
  // Appears only once something is expanded — the only state it can undo.
  const resetViewButton = expandedRegionIds.size > 0 ? (
    <button
      type="button"
      onClick={handleResetView}
      className="pointer-events-auto flex h-[28px] w-fit cursor-pointer items-center gap-[5px] rounded-[14px] bg-[#16A34A] px-[11px] font-['Inter'] text-[12px] font-semibold whitespace-nowrap text-white transition-colors hover:bg-[#12b152] dark:bg-[#22C55E] dark:text-[#191919] dark:hover:bg-[#1aa84f]"
    >
      <RotateCcw size={13} className="shrink-0" />
      {t('map.growing.resetView', 'Reset view')}
    </button>
  ) : null;

  const filterChips = (
    <MapFilterChips
      // In fullscreen this row is hoisted into the filter strip, where the
      // rule is one line of filters; elsewhere it has its own row and wraps.
      singleLine={isFullscreen}
      layerMode={layerMode}
      // What is actually drawn, not just a live view's set. Passing null for
      // the built-in modes sent the chips down their single-layer path, and
      // "all" is not in that lookup — so All fell through to the shipment
      // chips alone and never offered the weather row its own map was
      // rendering. `activeLayers` is the same memo the map reads, so the
      // chips and the pins can no longer disagree about what is on screen.
      activeLayers={activeLayers}
      productionStatusFilter={productionStatusFilter}
      activeLayer={activeLayer}
      onLayerChange={setActiveLayer}
      activeEventTypes={activeEventTypes}
      onEventTypeToggle={handleEventTypeToggle}
      chipFilters={chipFilters}
      originFilter={originFilter}
      destFilter={destFilter}
      modeFilter={modeFilter}
    />
  );

  return (
    <div
      className="relative flex h-full flex-col overflow-hidden max-lg:bg-dark-app"
    >
      {/* Global filter strip — single source of truth for commodity + status,
          and home for the fullscreen toggle (kept visible in both modes so
          users can exit fullscreen). Sits on the page background, above the
          card, with the fullscreen button pushed to the right edge.
          Mobile (<lg): the drawer hamburger sits on the SAME row, to the
          filters' left, directly on this page's gradient background — no
          separate header bar (DashboardLayout hides its standalone header
          for this route; see `isMapPage` there). Desktop (lg+): the button
          is `lg:hidden`, present in the DOM but never shown, so the row
          collapses back to exactly the original single-child layout. Hidden
          entirely in dashboard fullscreen (`isFullscreen`) since there's no
          drawer to open there. */}

      {/* Panel column + map. The panel sits beside the map (not inside it), so
          opening one narrows the map rather than splitting it. Page-level
          chrome stays visible in fullscreen — same model as Analytics, which
          keeps the chart's own scorecards in fullscreen and only hides the app
          sidebar and TopBar (handled in DashboardLayout). The map is now
          full-bleed on both breakpoints: no page padding, no card frame, every
          control floating on top (see the phone stack below). */}
      <div className="flex flex-1 min-h-0">
        <MapSidebar
          mode={activePanel?.startsWith('arrivals') ? 'arrivals' : activePanel}
          timeWindow={activePanel === 'arrivals3' ? 3 : activePanel === 'arrivals10' ? 10 : 7}
          onClose={() => setActivePanel(null)}
          shipments={arrivingShipments}
          weatherAlerts={weatherAlerts || []}
          extremeAlerts={extremeAlerts || []}
          harvestAlerts={harvestAlerts}
          onHoverRegion={setHoveredRegionId}
          onFocusRegion={setFocusedRegion}
          weightUnit={weightUnit}
          selectedCommodity={selectedCommodity}
        />

        <div className="relative min-w-0 flex flex-1 min-h-0 flex-col overflow-hidden">
          {/* Every control floats over the map in one column: the filter rows,
              the chip row and the collapsed Arrivals pill, top-left, in the
              order the design stacks them. `pointer-events-none` on the column
              keeps the gaps between controls clickable as map, so dragging the
              map still works under the stack. Phone: 16px inset, 8px gaps.
              Desktop: 24px inset, 12px gaps. */}
          {/* Bottom inset so the details popup has a height to fill: 24px from
              the map's bottom edge on desktop, 16px on phones. Harmless for the
              rows above it — the column is flex-col, justify-start. */}
          <div className="pointer-events-none absolute z-[1100] flex flex-col max-lg:left-[16px] max-lg:right-[16px] max-lg:top-[8px] max-lg:bottom-[16px] max-lg:gap-[8px] lg:inset-x-[24px] lg:top-[24px] lg:bottom-[24px] lg:gap-[12px]">
            {/* Two mounts rather than one responsive row: the phone stacks the
                controls over two lines and adds the drawer hamburger, which no
                amount of wrapping turns into the desktop's single row. */}
            {/* `relative` so Reset view can hang BELOW the strip's fullscreen
                toggle, per the design. It is absolute on purpose: as a flex
                child of the overlay column it mounted above the cluster panel,
                and the first Expand click pushed that panel down — the popup
                appearing to jump. Out of flow, it moves nothing. */}
            {/* No Status dropdown: production status is a Growing concept and
                the chip row already carries it when Growing is on. The old
                condition showed it whenever Growing was OFF — over Shipments
                and Weather, where harvest status filters nothing. */}
            <div className="pointer-events-auto relative hidden lg:block">
              <GlobalFilterStrip
                commodityVariant="map"
                showStatus={false}
                searchSlot={containerSearch}
                chipsSlot={hoistChips ? filterChips : null}
                isFullscreen={isFullscreen}
                onEnterFullscreen={() => setFullscreen(true)}
                onExitFullscreen={() => setFullscreen(false)}
                {...partyFilterProps}
              />
              {resetViewButton ? (
                <div className="absolute top-full right-0 mt-[12px] flex justify-end">
                  {resetViewButton}
                </div>
              ) : null}
            </div>
            <div className={`pointer-events-auto lg:hidden ${isFullscreen ? 'hidden' : ''}`}>
              <GlobalFilterStrip
                layout="map-mobile"
                commodityVariant="map"
                showStatus={false}
                searchSlot={containerSearch}
                mobileSearchSlot={mobileContainerSearch}
                menuSlot={isFullscreen ? null : (
                  <MobileMenuButton onClick={() => onMenuClick?.()} className="shrink-0" ariaLabel={t('nav.toggleMenu', 'Toggle menu')} />
                )}
                isFullscreen={isFullscreen}
                onEnterFullscreen={() => setFullscreen(true)}
                onExitFullscreen={() => setFullscreen(false)}
                {...partyFilterProps}
              />
            </div>

            {/* The chip row, for every case the strip does not cover.
                `hoistChips` only moves the chips into the DESKTOP strip — the
                phone strip (`layout="map-mobile"`) has no chip slot at all, so
                without this mount the chips vanished below lg in weather mode.
                Hoisted, this copy is `lg:hidden` and the strip's takes over
                from lg; the two are never visible at once, which is the same
                two-mount split GlobalFilterStrip itself uses. Fullscreen keeps
                its old behaviour of no second row at all. */}
            {(!hoistChips || !isFullscreen) && (
              <div className={`pointer-events-auto w-fit ${hoistChips ? 'lg:hidden' : ''}`}>
                {filterChips}
              </div>
            )}

            {/* Alerts, Arrivals and Season summary are one wrapping row, not
                three stacked blocks. As column children Alerts always took a
                line to itself however much room sat empty beside it; here the
                three sit together whenever the viewport fits them and break
                onto further lines only when it does not.

                `items-start` keeps each block its own height rather than
                stretching the pills to match an expanded neighbour, and
                `content-start` keeps wrapped lines packed at the top instead
                of spreading down the column's full height.

                `mt-[4px]` tops the column's 12px gap up to the 16px the frame
                shows. It belongs to the row so both blocks shift together;
                gated on Alerts because only the weather frame specifies it.

                The row claims the leftover height only while Arrivals is
                open, and while weather is drawn it also holds back the strip
                the timeline docks in — the timeline's own footprint (64px on
                desktop, 88px on phones) plus the 16px gap the design asks
                for, less the inset the column already carries. That bound is
                what the Arrivals panel caps itself against. */}
            {(showAlertsPanel || showArrivals) && (
            <div
              className={`flex flex-wrap content-start items-start gap-[12px] ${
                showAlertsPanel ? 'mt-[4px]' : ''
              } ${arrivalsOpen ? 'min-h-0 flex-1' : ''} ${
                arrivalsOpen && hasWeatherTimeline ? 'max-lg:pb-[88px] lg:pb-[56px]' : ''
              }`}
            >
            {/* Weather alerts sit beside Arrivals — the design draws them as
                the same strip.

                Weather only: the Growing design has no alerts widget. The
                panel still carries both tabs, so if it is ever wanted on
                Growing this gate is the only thing in the way.

                `min-h-0` lets the list scroll inside the panel rather than
                running past the timeline. */}
            {showAlertsPanel && (
              <div className={`pointer-events-auto flex min-h-0 w-fit flex-col ${isFullscreen ? 'max-lg:hidden' : ''}`}>
                <MapAlertsPanel
                  weatherAlerts={panelWeatherAlerts}
                  regionAlerts={panelRegionAlerts}
                  defaultTab="weather"
                  regionStatusLabel={regionStatusLabel}
                  onAskBiq={onAskBiq || null}
                />
              </div>
            )}

            {/* Arrivals counts inbound shipments, so it belongs to the layers
                that draw them. Weather and Growing show neither a vessel nor a
                plane, and a box count floating over them describes something
                not on screen. */}
            {showArrivals && (
            <div
              // Open, the panel is as tall as its content and no taller:
              // `max-h-full` bounds it to the row (which the row's own
              // `flex-1` and bottom padding have already cut to the space
              // above the timeline), and `min-h-0` lets it shrink so the
              // list inside scrolls once the content outgrows that. It used
              // to `self-stretch`, which filled the column whatever the
              // content was and left a tall band of white under a short day
              // list. Closed, the block must stay its natural size or the
              // pill row would stretch.
              ref={arrivalsWrapRef}
              className={`pointer-events-auto flex w-fit flex-col ${
                arrivalsOpen ? 'min-h-0 max-h-full' : ''
              } ${isFullscreen ? 'max-lg:hidden' : ''}`}
            >
              {/* Arrivals and Season summary share one row, per the design.
                  The expanded Arrivals panel hangs below Arrivals alone, so
                  the two pills sit in their own flex row above it rather than
                  the panel being pushed sideways by its neighbour. */}
              <div className="flex shrink-0 flex-wrap items-center gap-[12px]">
              {/* Mirrors the open panel's window: the strip is that panel
                  collapsed, so a 10-day selection must not read as 7d. */}
              <MapArrivalsStrip
                value={Math.round(arrivalTotal).toLocaleString()}
                unitLabel={t(`map.units.${weightUnit}`, weightUnit)}
                days={arrivalDays}
                open={arrivalsOpen}
                onToggle={() => setAllPanelOpen((o) => !o)}
              />
              <MapSeasonSummaryStrip onOpen={() => setSeasonSummaryOpen(true)} />
              </div>
              {arrivalsOpen && (
                <MapAllWidget
                  className="mt-[8px] min-h-0"
                  // "Arrivals", per the design — this panel is the Arrivals
                  // strip's open state, and the commodity it used to append is
                  // already named in the filter bar directly above it.
                  title={t('map.arrivals.title', 'Arrivals')}
                  onClose={() => setAllPanelOpen(false)}
                  windows={arrivalWindowStats}
                  groups={arrivalDayGroups}
                  days={arrivalDays}
                  onDaysChange={setArrivalDays}
                  total={arrivalTotal}
                  count={arrivalsByWindow[arrivalDays].length}
                  labels={{
                    close: t('common.close', 'Close'),
                    window: (d) => t('map.arrivals.window', '{{d}} days', { d }),
                    shipments: (n) => t('map.arrivals.shipments', '{{n}} shipments', { n }),
                    shipmentsStacked: (n) => t('map.arrivals.shipments', '{{n}} shipments', { n }),
                    total: (d) => t('map.arrivals.total', 'Total · {{d}} days', { d }),
                  }}
                  unit={weightUnit}
                  onUnitChange={setWeightUnit}
                  unitLabels={{
                    boxes: t('map.units.boxes', 'boxes'),
                    kg: t('map.units.kg', 'kg'),
                    lbs: t('map.units.lbs', 'lbs'),
                  }}
                />
              )}
            </div>
            )}
            </div>
            )}


            {/* Cluster overview. Sits directly under the filter chips, per the
                design; `mt-[4px]` tops the column's 12px gap up to the 16px
                the spec asks for. `min-h-0` lets its status list scroll inside
                the panel rather than pushing past the map's bottom edge. */}
            {selectedCluster && (
              <div className={`pointer-events-auto mt-[4px] flex min-h-0 w-full flex-col lg:w-[300px] ${isFullscreen ? 'max-lg:hidden' : ''}`}>
                <GrowingClusterOverview
                  cluster={selectedCluster}
                  onClose={() => setSelectedCluster(null)}
                  onZoomToRegions={handleZoomToCluster}
                  // Expanding leaves the panel open: its header is where the
                  // Reset that undoes it appears.
                  onExpandRegions={expandCluster}
                  onCollapseRegions={collapseCluster}
                  expanded={selectedClusterExpanded}
                  onAskBiq={onAskBiq ? (c) => onAskBiq(c) : null}
                />
              </div>
            )}

            {/* Details popup. Last in the column, so the column's 16px gap is
                what puts it below the Arrivals pill; `min-h-0` lets it take
                the remaining height down to the column's bottom inset and
                scroll inside rather than pushing past the map's edge. */}
            {selectedShipment && (
              <div className={`pointer-events-auto flex min-h-0 w-full flex-col lg:w-[344px] ${isFullscreen ? 'max-lg:hidden' : ''}`}>
                <ShipmentDetailsPanel
                  shipment={selectedShipment}
                  onClose={() => setSelectedShipment(null)}
                  onAskBiq={onAskBiq}
                />
              </div>
            )}
          </div>

          {/* The real modal, now that dev is merged in. `liveStatus` is the
              one prop with no source here: it comes from useSeasonLivePack,
              which ScorecardsBar owned — and this redesign removed that bar.
              The modal treats null as "no live pack", which is the honest
              state until a live hook is wired up on this layout. */}
          <SeasonSummaryModal
            isOpen={seasonSummaryOpen}
            onClose={() => setSeasonSummaryOpen(false)}
            canDownloadSeasonPdf={seasonPdfAllowed}
            selectedCommodity={selectedCommodity}
            email={email || ''}
            destSel={destSel}
            originSel={originSel}
            seasonBundle={seasonBundleData ?? null}
            seasonBundleLoading={seasonBundleLoading}
            seasonBundleError={seasonBundleError}
            liveStatus={null}
          />

          {isFullscreen && (
            /* Centred on the layer rail's column (rail is flush right and
               48px wide, so its tiles centre 24px in) rather than on the
               16px page inset — off-axis above the rail, the round button
               read as a stray control and its box clipped the top tile. */
            <div className="pointer-events-auto absolute right-[24px] top-[16px] z-[1100] translate-x-1/2 lg:hidden">
              <AnalyticsChartFullscreenButton
                round
                isActive
                onEnter={() => setFullscreen(true)}
                onExit={() => setFullscreen(false)}
              />
            </div>
          )}

          {/* Map container — receives already-commodity-filtered shipments so the
              scorecard counts above and the map markers below stay consistent. */}
          <div className="relative min-w-0 flex-1 min-h-0">
            <UnifiedMapContainer
              shipments={seaOn ? filteredShipments : EMPTY_SHIPMENTS}
              flights={airOn ? filteredFlights : EMPTY_SHIPMENTS}
              trucks={truckOn ? filteredTrucks : EMPTY_SHIPMENTS}
              seaOn={seaOn}
              layerMode={layerMode}
              onLayerModeChange={handleLayerModeSelect}
              activeLayers={activeLayers}
              savedViews={savedViews}
              activeViewId={activeViewId}
              onSelectView={setActiveViewId}
              onCombineSave={handleCombineSave}
              onUpdateView={handleUpdateView}
              onDeleteView={handleDeleteView}
              selectedShipment={selectedShipment}
              onShipmentSelect={setSelectedShipment}
              commodities={commodities}
              hoveredRegionId={hoveredRegionId}
              focusedRegion={focusedRegion}
              selectedCluster={selectedCluster}
              onClusterSelect={setSelectedCluster}
              zoomToCluster={zoomToCluster}
              expandedRegionIds={expandedRegionIds}
              resetViewSeq={resetViewSeq}
              selectedCommodity={selectedCommodity}
              selectedStatuses={selectedStatuses}
              onStatusesChange={setSelectedStatuses}
              zoomCountry={zoomCountry}
              zoomRegion={zoomRegion}
              mobileControlsExpanded={expandedSection === 'controls'}
              countryPredicate={countryPredicate}
              containerDetailOpen={containerDetailOpen}
              activeLayer={activeLayer}
              onLayerChange={setActiveLayer}
              activeEventTypes={activeEventTypes}
              onEventTypeToggle={handleEventTypeToggle}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapPage;
